<template>
    <div class="mb-50 pb-50" style="border-bottom: 1px solid #eee">
        <div class="columns">
            <div class="column is-3">
                <img :src="proposal.campaign.image" style="border-radius: 3px;" />
            </div>
            <div class="column is-6">
                <div class="title is-5">
                    {{ proposal.campaign.title }}
                </div>
                <div class="text-light text-14 text-italic">
                    You submitted this proposal on {{ proposal.submitted_at }}.
                </div>

                <div class="mt-10">
                    <div class="mb-5" v-if="proposal.price">
                        <strong class="text-16 mb-2">Your Price:</strong><br>
                        <span class="has-text-green">
                            <money-display :value="proposal.price" :currency="proposal.campaign.brand.currency" />
                        </span>
                    </div>
                    <div>
                        <strong class="text-16">Your Notes:</strong><br>
                        <span class="text-14">{{ proposal.notes }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-15 notification" v-if="!accepted && !declined">
            <strong>
                {{ customizationText('proposal_submitted_header', 'Waiting on Confirmation') }}
            </strong>
            <br>
            {{ customizationText('proposal_submitted_text', 'You have submitted a proposal and it is currently under review. You will get an email when the status changes.') }}
        </div>

        <div class="mt-15 notification" v-if="accepted">
            <strong>{{ customizationText('proposal_accepted_header', 'You have been accepted!') }}</strong>
            <br>
            {{ customizationText('proposal_accepted_text', 'You can now see the tasks required in order to complete this opportunity.') }}
            <div class="mt-5">
                <router-link
                    v-if="proposal.campaign.hasOwnProperty('version') && proposal.campaign.version === 2"
                    :to="{ name: 'v2.campaign.proposal', params: { campaign_id: proposal.campaign.id, proposal_id: proposal.id }}"
                    target="_blank"
                    class="no-underline"
                >
                    <button class="button is-small">
                        View Details
                    </button>
                </router-link>
                <button v-else class="button is-small" @click="$root.showJob(proposal.id)">
                    View Details
                </button>
            </div>
        </div>

        <div class="mt-20 text-center" v-if="proposal.campaign.is_active && !accepted">
            <button class="button is-small is-outlined" :class="{'is-loading': deleting === proposal.id}" @click="deleteProposal(proposal)">
                Delete & Resubmit This Proposal
            </button>
        </div>

    </div>
</template>

<script>
    import CampaignCustomizeMixin from '../../../mixins/CampaignCustomizeMixin';
    import MoneyDisplay from '../../interface/elements/MoneyDisplay.vue';

    export default {

        /**
         * The component name
         *
         * @type {string}
         */
        name: 'ProposalListRow',

        /**
         * The component mixins
         *
         * @type {array}
         */
        mixins: [
            CampaignCustomizeMixin
        ],

        /**
         * The child components
         *
         * @type {object}
         */
        components: {
            MoneyDisplay
        },

        /**
         * The component properties
         *
         * @type {object}
         */
        props: {

            /**
             * The proposals
             */
            proposal: {
                type: Object,
                required: true
            }
        },

        /**
         * The component data
         *
         * @returns {object}
         */
        data() {
            return {
                deleting: null
            }
        },

        /**
         * The computed properties
         *
         * @type {object}
         */
        computed: {

            /**
             * The campaign id
             *
             * @returns {string}
             */
            campaign_id() {
                return this.$route.params.campaign_id;
            },

            /**
             * The campaign
             *
             * @returns {object}
             */
            campaign() {
                return this.$root.getCampaign(this.campaign_id);
            },

            /**
             * Whether the proposal was accepted
             *
             * @returns {boolean}
             */
            accepted() {
                return this.proposal.accepted_at !== null;
            },

            /**
             * Whether the proposal was accepted
             *
             * @returns {boolean}
             */
            declined() {
                return this.proposal.declined_at !== null;
            }
        },

        /**
         * The component methods
         *
         * @type {object}
         */
        methods: {

            /**
             * Delete a proposal
             *
             * @param {Object} proposal
             */
            deleteProposal(proposal) {
                this.deleting = proposal.id;
                if (this.campaign.hasOwnProperty('version') && this.campaign.version === 2) {
                    this.$http.delete(`/api/v1/contacts/${proposal.contact_id}/campaigns/${this.campaign_id}/proposals/${proposal.id}`)
                        .then(() => {
                            this.$notifySuccess('Your proposal has been removed, you can now re-submit');
                            this.$root.go('v2.campaign', { campaign_id: this.campaign_id });
                        }, () => {
                            this.$notifyError('Could not remove proposal, please try again or contact support');
                            this.deleting = null;
                        });
                } else {
                    this.$http.post(`/campaigns/proposal/${proposal.id}/delete`).then(() => {
                        this.$notifySuccess('Your proposal has been removed, you can now re-submit');
                        this.$root.reloadContact('proposals');
                        this.$root.go('campaign', { campaign_id: this.campaign_id });
                    }, () => {
                        this.$notifyError('Could not remove proposal, please try again or contact support');
                        this.deleting = null;
                    });
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .notification {
        & ::v-deep a.no-underline {
            text-decoration: none;
        }
    }
</style>

